import CampaignFilledIcon from '@mui/icons-material/Campaign'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import CheckCircleFilledIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import CreditCardFilledIcon from '@mui/icons-material/CreditCard'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import CurrencyYenFilledIcon from '@mui/icons-material/CurrencyYen'
import CurrencyYenOutlinedIcon from '@mui/icons-material/CurrencyYenOutlined'
import FiberNewIcon from '@mui/icons-material/FiberNew'
import HomeFilledIcon from '@mui/icons-material/Home'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import OpenInNew from '@mui/icons-material/OpenInNew'
import ReceiptLongFilledIcon from '@mui/icons-material/ReceiptLong'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import ShowChartFilledIcon from '@mui/icons-material/ShowChart'
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined'
import SmsFilledIcon from '@mui/icons-material/Sms'
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined'
import { Badge, Divider, ListItemIcon, ListItemText, MenuItem, MenuList, Stack } from '@mui/material'
import { DateTime } from 'luxon'
import NextLink from 'next/link'
import { useRouter } from 'next/router'
import { ReactNode } from 'react'
import { useTodoOffersQuery } from '~/generated/graphql'
import { pagesPath } from '~/lib/$path'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'
import { CreateCampaignButton } from './CreateCampaignButton'

type MenuRoute = {
  path: string
  text: string
  Icon: (props: { selected: boolean; showBadge?: boolean }) => ReactNode
  showNewIconUntil?: DateTime
}

const iconStyle = {
  color: 'rgb(0, 87, 183)',
  fill: 'rgb(0, 87, 183)',
}

const userMenuRoutes: readonly MenuRoute[] = [
  {
    path: pagesPath.home.$url().pathname,
    text: 'ホーム',
    Icon: ({ selected }) => (selected ? <HomeFilledIcon sx={iconStyle} /> : <HomeOutlinedIcon sx={iconStyle} />),
  },
  {
    path: pagesPath.campaigns.$url().pathname,
    text: 'キャンペーン一覧',
    Icon: ({ selected }) =>
      selected ? <CampaignFilledIcon sx={iconStyle} /> : <CampaignOutlinedIcon sx={iconStyle} />,
  },
  {
    path: pagesPath.offers.$url().pathname,
    text: 'やりとり一覧',
    Icon: ({ selected, showBadge }) => (
      <Badge
        variant="dot"
        color="error"
        invisible={!showBadge}
        sx={{
          '& .MuiBadge-badge': {
            height: 8,
            minWidth: 8,
            top: 4,
            right: 4,
          },
        }}
      >
        {selected ? <SmsFilledIcon sx={iconStyle} /> : <SmsOutlinedIcon sx={iconStyle} />}
      </Badge>
    ),
  },
  {
    path: pagesPath.offer_reports.$url().pathname,
    text: '承認した完了報告',
    Icon: ({ selected }) =>
      selected ? <CheckCircleFilledIcon sx={iconStyle} /> : <CheckCircleOutlinedIcon sx={iconStyle} />,
  },
  {
    path: pagesPath.offline_report.$url().pathname,
    text: 'レポートのダウンロード',
    Icon: ({ selected }) =>
      selected ? <ShowChartFilledIcon sx={iconStyle} /> : <ShowChartOutlinedIcon sx={iconStyle} />,
  },
  {
    path: pagesPath.contract.$url().pathname,
    text: 'チケットの購入・契約',
    Icon: ({ selected }) =>
      selected ? <CurrencyYenFilledIcon sx={iconStyle} /> : <CurrencyYenOutlinedIcon sx={iconStyle} />,
  },
  {
    path: pagesPath.subscriptions.$url().pathname,
    text: '所持チケット・契約確認',
    Icon: ({ selected }) =>
      selected ? <CreditCardFilledIcon sx={iconStyle} /> : <CreditCardOutlinedIcon sx={iconStyle} />,
  },
  {
    path: pagesPath.invoice.$url().pathname,
    text: '各月の請求',
    Icon: ({ selected }) =>
      selected ? <ReceiptLongFilledIcon sx={iconStyle} /> : <ReceiptLongOutlinedIcon sx={iconStyle} />,
  },
] as const

const otherLinks = [
  {
    path: pagesPath.beginner.instagram.$url().pathname,
    text: 'ビギナーガイド',
    openInNew: false,
  },
  {
    path: EXTERNAL_LINKS.LINE_OFFICIAL,
    text: 'LINEチャットサポート',
    openInNew: true,
  },
  {
    path: EXTERNAL_LINKS.CASE_STUDIES,
    text: '他社事例紹介',
    openInNew: true,
  },
  {
    path: EXTERNAL_LINKS.LIKES_APPLICATION,
    text: 'likesのお申し込みはこちら',
    openInNew: true,
  },
  {
    path: pagesPath.cancellations.subscription.$url().pathname,
    text: '解約について',
    openInNew: false,
  },
] as const

export const AppDrawerContent = () => {
  const { asPath } = useRouter()
  const { data: todoOffersData } = useTodoOffersQuery()

  return (
    <Stack>
      <CreateCampaignButton />
      <MenuList sx={{ pt: 0 }}>
        {userMenuRoutes.map(({ path, text, Icon, showNewIconUntil }, i) => (
          <NextLink href={path} passHref key={path} legacyBehavior>
            <MenuItem
              // 指定しないとなぜかtab keyによるfocusが当たらない
              tabIndex={i === 0 ? 0 : undefined}
              key={path}
              focusRipple
              component="a"
              selected={asPath.startsWith(path)}
              sx={{ height: 50, position: 'relative' }}
            >
              <ListItemIcon color="primary">
                {Icon({
                  selected: asPath.startsWith(path),
                  showBadge:
                    path === pagesPath.offers.$url().pathname &&
                    (todoOffersData?.viewer.company?.todoOffers?.length ?? 0) > 0,
                })}
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  fontSize: 'small',
                  fontWeight: 'bold',
                }}
              >
                {text}
              </ListItemText>
              {showNewIconUntil && DateTime.now() < showNewIconUntil && (
                <FiberNewIcon
                  sx={{
                    position: 'absolute',
                    right: 10,
                    color: 'error.main',
                    fontSize: 30,
                  }}
                />
              )}
            </MenuItem>
          </NextLink>
        ))}
      </MenuList>
      <Divider />
      <MenuList>
        {otherLinks.map(({ path, text, openInNew }) =>
          openInNew ? (
            <MenuItem
              focusRipple
              component="a"
              href={path}
              key={path}
              target="_blank"
              rel="noreferrer"
              sx={{ height: 40 }}
            >
              <ListItemText primaryTypographyProps={{ fontSize: 'small' }}>{text}</ListItemText>
              <ListItemIcon>
                <OpenInNew style={{ fontSize: 'small' }} />
              </ListItemIcon>
            </MenuItem>
          ) : (
            <NextLink href={path} passHref key={path} legacyBehavior>
              <MenuItem focusRipple component="a" key={path} sx={{ height: 40 }}>
                <ListItemText primaryTypographyProps={{ fontSize: 'small' }}>{text}</ListItemText>
              </MenuItem>
            </NextLink>
          ),
        )}
      </MenuList>
    </Stack>
  )
}
